import React, { useState, useEffect, useCallback } from 'react'
import { CitiesContext } from './CitiesContext'
import { fetchCantonesWithTenants } from '../services/cantonService'
import { updateValidSlugs } from '../utils/cityDetector'

const STORAGE_KEY = 'ArbolEc_selectedTenant'

// Fallback de cantones por si falla la API
const FALLBACK_CANTONES = [
  {
    id: 1,
    slug: 'loja',
    name: 'Loja',
    center: [-79.20221, -3.99523],
    zoom: 12,
    bounds: [
      [-79.35, -4.25],
      [-79.05, -3.75],
    ],
    enabled: true,
    tenants: [],
  },
]

const CitiesProvider = ({ children }) => {
  const [cantones, setCantones] = useState([])
  const [defaultCanton, setDefaultCanton] = useState(null)
  const [cantonesStatus, setCantonesStatus] = useState('loading') // loading | ok | empty | error
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedTenant, setSelectedTenant] = useState(() => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) || null
    } catch {
      return null
    }
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const applyCantones = useCallback(data => {
    setCantones(data)
    setDefaultCanton(data[0] || null)
    updateValidSlugs(data.map(c => c.slug))
  }, [])

  const loadCantones = useCallback(async (options = {}) => {
    const { force = false, noCache = false } = options

    try {
      setLoading(true)
      setError(null)
      setCantonesStatus('loading')

      let cantonesData = await fetchCantonesWithTenants({
        force,
        noCache,
        throwOnError: true,
      })

      if (!cantonesData || cantonesData.length === 0) {
        // Reintentar saltando cache (evita respuestas cacheadas vacías)
        cantonesData = await fetchCantonesWithTenants({
          force: true,
          noCache: true,
          throwOnError: true,
        })
      }

      if (cantonesData && cantonesData.length > 0) {
        applyCantones(cantonesData)
        setCantonesStatus('ok')
        return
      }

      console.warn('No cantones from API, using fallback')
      applyCantones(FALLBACK_CANTONES)
      setCantonesStatus('empty')
    } catch (err) {
      console.error('Failed to load cantones:', err)
      setError(err?.message || 'Error al cargar cantones')
      applyCantones(FALLBACK_CANTONES)
      setCantonesStatus('error')
    } finally {
      setLoading(false)
    }
  }, [applyCantones])

  const reloadCantones = useCallback(() => {
    loadCantones({ force: true, noCache: true })
  }, [loadCantones])

  useEffect(() => {
    loadCantones()
  }, [loadCantones])

  // === NUEVAS FUNCIONES PARA CANTONES ===

  const getEnabledCantones = useCallback(() => cantones, [cantones])

  const getCantonBySlug = useCallback(
    slug => cantones.find(c => c.slug === slug),
    [cantones]
  )

  const getDefaultCanton = useCallback(
    () => defaultCanton || cantones[0],
    [defaultCanton, cantones]
  )

  const getValidSlugs = useCallback(() => cantones.map(c => c.slug), [cantones])

  const isValidCantonSlug = useCallback(
    slug => cantones.some(c => c.slug === slug),
    [cantones]
  )

  const getTenantsByCantonSlug = useCallback(
    slug => {
      const canton = cantones.find(c => c.slug === slug)
      return canton?.tenants || []
    },
    [cantones]
  )

  // Seleccionar tenant (null para "Todos") — persiste en sessionStorage (scoped a la pestaña/sesión)
  const selectTenant = useCallback(tenantSlug => {
    const value = tenantSlug || null
    setSelectedTenant(value)
    try {
      if (value) window.sessionStorage.setItem(STORAGE_KEY, value)
      else window.sessionStorage.removeItem(STORAGE_KEY)
    } catch { /* ignore */ }
  }, [])

  // Resetear tenant al cambiar de cantón — limpia sessionStorage
  const resetTenant = useCallback(() => {
    setSelectedTenant(null)
    try { window.sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
  }, [])

  const selectCountry = useCallback(iso2 => setSelectedCountry(iso2 || null), [])
  const resetCountry = useCallback(() => setSelectedCountry(null), [])

  const getUniqueCountries = useCallback(() => {
    const seen = new Set()
    return cantones.reduce((acc, c) => {
      const iso2 = c.countryIso2
      if (iso2 && !seen.has(iso2)) {
        seen.add(iso2)
        // Usar center/zoom del backend si están configurados; si no, calcular center
        // como promedio de cantones como fallback
        const center = c.countryCenter || (() => {
          const cantonCenters = cantones.filter(x => x.countryIso2 === iso2 && Array.isArray(x.center))
          return cantonCenters.length > 0
            ? [
                cantonCenters.reduce((s, x) => s + x.center[0], 0) / cantonCenters.length,
                cantonCenters.reduce((s, x) => s + x.center[1], 0) / cantonCenters.length,
              ]
            : null
        })()
        acc.push({ iso2, icono_svg: c.countryIcon || null, center, zoom: c.countryZoom ?? 6, label_n2: c.countryLabelN2 || null })
      }
      return acc
    }, [])
  }, [cantones])

  // === FUNCIONES DE COMPATIBILIDAD (API legacy) ===
  // Estas funciones mantienen la compatibilidad con el código existente
  // que espera "cities" en lugar de "cantones"

  const getEnabledCities = useCallback(() => {
    return cantones.map(c => ({
      slug: c.slug,
      name: c.name,
      fullName: c.name,
      center: c.center,
      zoom: c.zoom,
      bounds: c.bounds,
      enabled: c.enabled,
      icono_svg: c.countryIcon || null,
      countryIso2: c.countryIso2 || null,
    }))
  }, [cantones])

  const getCityBySlug = useCallback(
    slug => {
      const canton = cantones.find(c => c.slug === slug)
      if (!canton) return null
      return {
        slug: canton.slug,
        name: canton.name,
        fullName: canton.name,
        center: canton.center,
        zoom: canton.zoom,
        bounds: canton.bounds,
        enabled: canton.enabled,
      }
    },
    [cantones]
  )

  const getDefaultCity = useCallback(() => {
    const canton = defaultCanton || cantones[0]
    if (!canton) return null
    return {
      slug: canton.slug,
      name: canton.name,
      fullName: canton.name,
      center: canton.center,
      zoom: canton.zoom,
      bounds: canton.bounds,
      enabled: canton.enabled,
    }
  }, [defaultCanton, cantones])

  const value = {
    // Nuevo sistema de cantones
    cantones,
    defaultCanton,
    cantonesStatus,
    selectedTenant,
    loading,
    error,
    reloadCantones,

    // Funciones para cantones
    getEnabledCantones,
    getCantonBySlug,
    getDefaultCanton,
    getValidSlugs,
    isValidCantonSlug,
    getTenantsByCantonSlug,
    selectTenant,
    resetTenant,

    // País seleccionado
    selectedCountry,
    selectCountry,
    resetCountry,
    getUniqueCountries,

    // COMPATIBILIDAD: API legacy (cities)
    cities: getEnabledCities(),
    defaultCity: getDefaultCity(),
    getEnabledCities,
    getCityBySlug,
    getDefaultCity,
    isValidCitySlug: isValidCantonSlug,
  }

  return (
    <CitiesContext.Provider value={value}>{children}</CitiesContext.Provider>
  )
}

export default CitiesProvider
